import { useState, useEffect, useMemo, useCallback } from 'react';
import { CONSTANTS } from '../data.js';
import { calculateCoinsPerSecond } from '../gameLogic/dragonLogic.js';
import { checkForNewUnlocks } from '../gameLogic/unlockLogic.js';

export const useGameState = (showNotification) => {
  const [coins, setCoins] = useState(CONSTANTS.INITIAL_COINS);
  const [keys, setKeys] = useState(CONSTANTS.INITIAL_KEYS);
  const [playerDragons, setPlayerDragons] = useState([]);
  const [maxDragons, setMaxDragons] = useState(CONSTANTS.INITIAL_MAX_DRAGONS);
  const [shopCosts, setShopCosts] = useState(() => {
    const initialCosts = {};
    Object.values(CONSTANTS.DRAGONS).forEach(d => {
      initialCosts[d.name] = d.value;
    });
    return initialCosts;
  });
  const [unlockedDragons, setUnlockedDragons] = useState(() =>
    Object.values(CONSTANTS.DRAGONS).filter(d => d.rarity === 'Basic').map(d => d.name)
  );
  const [unlockedRecipes, setUnlockedRecipes] = useState([]);
  const [coinAnimation, setCoinAnimation] = useState(false);
  const [selectedBreedingDragons, setSelectedBreedingDragons] = useState([]);
  const [activeTab, setActiveTab] = useState('inventory');
  const [indexUnlockedDragons, setIndexUnlockedDragons] = useState(() =>
    Object.values(CONSTANTS.DRAGONS).filter(d => d.rarity === 'Basic').map(d => d.name)
  );
  const [penaltyInfo, setPenaltyInfo] = useState(null);
  const [fusionResult, setFusionResult] = useState(null);

  // Battle System State
  const [battleProgress, setBattleProgress] = useState(() => 
    new Array(CONSTANTS.COMPETITORS.length).fill(0)
  );
  const [battle, setBattle] = useState({
    phase: 'map', // map, preparation, active, face-off, result
    round: 0,
    playerScore: 0,
    opponentScore: 0,
    playerTeam: [],
    opponentTeam: [],
    log: [],
    turn: 'player',
    opponentAction: null,
    result: null,
    opponent: null,
    faceOff: null,
  });
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [selectedBattleTeam, setSelectedBattleTeam] = useState([]);

  const coinsPerSecond = useMemo(() => calculateCoinsPerSecond(playerDragons), [playerDragons]);

  // Auto coin generation
  useEffect(() => {
    const interval = setInterval(() => {
      if (coinsPerSecond > 0) {
        setCoins(prevCoins => prevCoins + coinsPerSecond);
        setCoinAnimation(true);
        setTimeout(() => setCoinAnimation(false), 500);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [coinsPerSecond]);

  // Auto-unlock dragons that are in inventory
  useEffect(() => {
    const dragonsInInventory = playerDragons.map(pd => pd.name);
    const newUnlocks = dragonsInInventory.filter(name => !unlockedDragons.includes(name));
    
    if (newUnlocks.length > 0) {
      setUnlockedDragons(prev => [...prev, ...newUnlocks]);
    }
  }, [playerDragons, unlockedDragons]);

  // Auto-unlock dragons in Index when added to inventory
  useEffect(() => {
    const dragonsInInventory = playerDragons.map(pd => pd.name);
    const newIndexUnlocks = dragonsInInventory.filter(name => !indexUnlockedDragons.includes(name));
    
    if (newIndexUnlocks.length > 0) {
      setIndexUnlockedDragons(prev => [...prev, ...newIndexUnlocks]);
    }
  }, [playerDragons, indexUnlockedDragons]);

  // Divine and Transcendental Dragon Unlock Logic
  useEffect(() => {
    const newUnlocks = checkForNewUnlocks(unlockedDragons);

    if (newUnlocks.length > 0) {
      setUnlockedDragons(prev => {
        const currentUnlocks = new Set(prev);
        newUnlocks.forEach(unlock => currentUnlocks.add(unlock));
        return Array.from(currentUnlocks);
      });
      newUnlocks.forEach(name => {
        const dragonData = CONSTANTS.DRAGONS[name];
        showNotification(`A new ${dragonData.rarity} dragon is available: ${name}!`, 'success');
      });
    }
  }, [unlockedDragons, showNotification]);

  return {
    coins, setCoins,
    keys, setKeys,
    playerDragons, setPlayerDragons,
    maxDragons, setMaxDragons,
    shopCosts, setShopCosts,
    unlockedDragons, setUnlockedDragons,
    unlockedRecipes, setUnlockedRecipes,
    coinAnimation, setCoinAnimation,
    selectedBreedingDragons, setSelectedBreedingDragons,
    activeTab, setActiveTab,
    indexUnlockedDragons, setIndexUnlockedDragons,
    penaltyInfo, setPenaltyInfo,
    fusionResult, setFusionResult,
    battleProgress, setBattleProgress,
    battle, setBattle,
    selectedCompetitor, setSelectedCompetitor,
    selectedBattleTeam, setSelectedBattleTeam,
    coinsPerSecond
  };
};
